// src/components/dashboard/FreeAgentSpotlight.js
// Desktop version of the Free Agent Upgrade Spotlight.
// Calls the same /api/players/leagues/{id}/free-agents/spotlight endpoint
// as the mobile component.
import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import PlayerLink from '../PlayerLink';

const fmtZ = (z) => {
  if (z == null) return '--';
  return (z >= 0 ? '+' : '') + z.toFixed(2);
};

const fmtStat = (val, decimals) => {
  if (val == null) return '--';
  return typeof val === 'number' && decimals != null ? val.toFixed(decimals) : val;
};

const keyStat = (stats, isPitcher) => {
  if (!stats) return null;
  if (isPitcher) {
    if (stats.era != null) return { label: 'ERA', value: fmtStat(stats.era, 2) };
    if (stats.whip != null) return { label: 'WHIP', value: fmtStat(stats.whip, 2) };
    return null;
  }
  if (stats.batting_avg != null) return { label: 'AVG', value: fmtStat(stats.batting_avg, 3) };
  if (stats.ops != null) return { label: 'OPS', value: fmtStat(stats.ops, 3) };
  return null;
};

const FreeAgentSpotlight = ({ leagues = [] }) => {
  const [upgrades, setUpgrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leagueId, setLeagueId] = useState(null);

  useEffect(() => {
    if (leagues.length > 0) {
      setLeagueId(leagues[0].league_id);
    } else {
      setLoading(false);
    }
  }, [leagues]);

  useEffect(() => {
    if (leagueId) {
      fetchSpotlight();
    }
  }, [leagueId]);

  const fetchSpotlight = async () => {
    try {
      setLoading(true);
      const res = await leaguesAPI.getFreeAgentSpotlight(leagueId);
      setUpgrades(res?.success ? (res.upgrades || []) : []);
    } catch (err) {
      console.error('Error fetching FA spotlight:', err);
      setUpgrades([]);
    } finally {
      setLoading(false);
    }
  };

  if (!leagues.length) return null;

  if (loading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center space-x-2 mb-3">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>Upgrade Spotlight</h3>
        </div>
        <div className="flex justify-center items-center py-8">
          <div className="flex items-center space-x-3">
            <div className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin ${dynastyTheme.classes.border.primaryBright}`} />
            <span className={dynastyTheme.classes.text.neutralLight}>Finding upgrades...</span>
          </div>
        </div>
      </div>
    );
  }

  if (upgrades.length === 0) return null;

  return (
    <div className={`${dynastyTheme.components.card.base} p-4`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <h3 className={`font-semibold ${dynastyTheme.classes.text.white}`}>Upgrade Spotlight</h3>
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight} bg-green-600/20 px-2 py-1 rounded`}>
            LIVE DATA
          </span>
        </div>
        {leagues.length > 1 && (
          <select
            value={leagueId || ''}
            onChange={(e) => setLeagueId(e.target.value)}
            className={`text-xs px-2 py-1 rounded ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral}`}
          >
            {leagues.map(l => (
              <option key={l.league_id} value={l.league_id}>{l.league_name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
        {upgrades.map((u, i) => {
          const fa = u.recommended_fa;
          const cur = u.current_player;
          const isPitcher = u.position === 'P';
          const accentColor = isPitcher ? 'text-blue-400' : 'text-yellow-400';
          const accentBg = isPitcher ? 'bg-blue-400/10' : 'bg-yellow-400/10';
          const curStat = keyStat(cur?.stats, isPitcher);
          const faStat = keyStat(fa?.stats, isPitcher);

          return (
            <div
              key={fa?.player_id || i}
              className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg border border-neutral-700/40 hover:border-yellow-400/40 transition-all duration-200 flex-shrink-0 p-3`}
              style={{ minWidth: 180, maxWidth: 200 }}
            >
              {/* Position badge + Z-score delta */}
              <div className="flex justify-between items-center mb-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${accentBg} ${accentColor}`}>
                  {u.position}
                </span>
                <span className="text-xs font-bold text-green-400">
                  +{u.z_score_delta?.toFixed(2)}
                </span>
              </div>

              {/* Current player (muted) */}
              <div className="mb-2">
                <p className={`text-[10px] ${dynastyTheme.classes.text.neutralLighter} truncate`}>
                  {cur?.player_name || '?'}{curStat ? ` \u00B7 ${curStat.value} ${curStat.label}` : ''}
                </p>
                <p className={`text-[10px] ${dynastyTheme.classes.text.neutral}`}>
                  z {fmtZ(cur?.aggregate_z_score)}
                </p>
              </div>

              {/* Arrow */}
              <div className="text-center text-[10px] font-bold text-green-400 my-1">
                &#8595; upgrade
              </div>

              {/* Recommended FA (prominent) */}
              <div className="truncate mb-1">
                <PlayerLink
                  mlbId={fa?.player_id}
                  name={fa?.player_name || '?'}
                  style={{ fontWeight: 700, fontSize: '0.8rem', color: '#f9fafb' }}
                />
              </div>
              <p className={`text-[10px] ${dynastyTheme.classes.text.neutralLight} mb-1`}>
                {fa?.mlb_team || '--'} &middot; {fa?.position || '??'}
              </p>

              {faStat && (
                <p className={`text-xs font-semibold ${isPitcher ? 'text-blue-400' : 'text-green-400'} mb-1`}>
                  {faStat.value} {faStat.label}
                </p>
              )}

              {/* Z-score + price */}
              <div className="flex justify-between items-baseline">
                <span className={`text-[10px] font-semibold ${accentColor}`}>
                  z {fmtZ(fa?.aggregate_z_score)}
                </span>
                {fa?.baseline_price != null && (
                  <span className={`text-sm font-bold ${accentColor}`}>
                    ${Math.round(fa.baseline_price)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className={`mt-3 text-center text-xs ${dynastyTheme.classes.text.neutralLighter}`}>
        Based on z-score analysis of your roster vs available free agents
      </div>
    </div>
  );
};

export default FreeAgentSpotlight;
